import { useEffect, useState, type FormEvent } from 'react';
import type { Asset, AssetType } from '@stfors/shared-types';
import { Modal } from './Modal';
import { DateInput } from './DateInput';
import { MoneyInput } from './MoneyInput';
import { LookupSelect } from './LookupSelect';
import { ApiError } from '../api/client';
import { apiErrorText, parseValidationError } from '../utils/apiErrors';
import { useUpdateAsset } from '../api/queries/assets';
import './EditAssetModal.css';

interface Props {
  open: boolean;
  asset: Asset;
  onClose: () => void;
}

// ONLY the keys this form actually renders a `<span className="field-error">`
// for. Listing a field here that has no slot is worse than omitting it: the
// message would be routed to an inline slot that doesn't exist and vanish.
// `asset_type` is the case that matters — it's a <LookupSelect> with no error
// slot, and a rejected asset_type is precisely what this form has to explain.
const HIGHLIGHTABLE_FIELDS = ['name', 'purchase_price'] as const;

export function EditAssetModal({ open, asset, onClose }: Props): JSX.Element {
  const update = useUpdateAsset();

  const [name, setName] = useState('');
  const [assetType, setAssetType] = useState<AssetType>('write_blocker');
  const [serial, setSerial] = useState('');
  const [makeModel, setMakeModel] = useState('');
  const [purchaseDate, setPurchaseDate] = useState<number | null>(null);
  const [price, setPrice] = useState('');
  const [warranty, setWarranty] = useState<number | null>(null);
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [topError, setTopError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    setName(asset.name);
    setAssetType(asset.asset_type);
    setSerial(asset.serial_number ?? '');
    setMakeModel(asset.make_model ?? '');
    setPurchaseDate(asset.purchase_date);
    setPrice(asset.purchase_price ?? '');
    setWarranty(asset.warranty_expiry);
    setLocation(asset.location ?? '');
    setNotes(asset.notes ?? '');
    setTopError(null);
    setFieldErrors({});
  }, [open, asset]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTopError(null);
    setFieldErrors({});

    if (name.trim().length === 0) {
      setFieldErrors({ name: 'Name is required' });
      return;
    }
    const trimmedPrice = price.trim();
    if (trimmedPrice && !/^\d+(\.\d{1,2})?$/.test(trimmedPrice)) {
      setFieldErrors({ purchase_price: 'Enter an amount like 1499.00' });
      return;
    }
    if (trimmedPrice && !(Number(trimmedPrice) > 0)) {
      setFieldErrors({ purchase_price: 'Amount must be greater than 0 (leave blank to clear)' });
      return;
    }

    try {
      await update.mutateAsync({
        id: asset.id,
        name: name.trim(),
        asset_type: assetType,
        serial_number: serial.trim() || null,
        make_model: makeModel.trim() || null,
        purchase_date: purchaseDate,
        purchase_price: trimmedPrice || null,
        warranty_expiry: warranty,
        location: location.trim() || null,
        notes: notes.trim() || null,
      });
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setTopError("You don't have permission to edit assets.");
        return;
      }
      // parseValidationError highlights the fields this form actually renders
      // and folds everything else — root-level errors included — into the
      // banner, so a rejected asset_type says which value was refused instead
      // of "Could not save the asset. Try again."
      const failure = parseValidationError(err, HIGHLIGHTABLE_FIELDS);
      if (failure) {
        setFieldErrors(failure.fieldErrors);
        setTopError(failure.message);
        return;
      }
      setTopError(apiErrorText(err, 'Could not save the asset.'));
    }
  };

  const busy = update.isPending;

  return (
    <Modal
      title={`Edit Asset — ${asset.name}`}
      open={open}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button type="button" className="btn-charcoal-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="edit-asset-form" className="btn-cyan-primary" disabled={busy}>
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </>
      }
    >
      <form id="edit-asset-form" className="eam-form" onSubmit={onSubmit} noValidate>
        <div className="eam-row">
          <label className="eam-field eam-field-wide">
            <span>Name</span>
            <input
              type="text"
              className="form-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              disabled={busy}
            />
            {fieldErrors['name'] && <span className="field-error">{fieldErrors['name']}</span>}
          </label>
          <label className="eam-field">
            <span>Type</span>
            <LookupSelect
              category="asset_type"
              value={assetType}
              onChange={setAssetType}
              className="form-input"
              disabled={busy}
            />
          </label>
        </div>

        <div className="eam-row">
          <label className="eam-field">
            <span>Serial</span>
            <input
              type="text"
              className="form-input"
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
              maxLength={120}
              disabled={busy}
            />
          </label>
          <label className="eam-field">
            <span>Make / model</span>
            <input
              type="text"
              className="form-input"
              value={makeModel}
              onChange={(e) => setMakeModel(e.target.value)}
              maxLength={200}
              disabled={busy}
            />
          </label>
        </div>

        <div className="eam-row">
          <label className="eam-field">
            <span>Purchase date</span>
            <DateInput value={purchaseDate} onChange={setPurchaseDate} disabled={busy} />
          </label>
          <label className="eam-field">
            <span>Purchase price (USD)</span>
            <MoneyInput value={price} onChange={setPrice} placeholder="1499.00" disabled={busy} />
            {fieldErrors['purchase_price'] && (
              <span className="field-error">{fieldErrors['purchase_price']}</span>
            )}
          </label>
          <label className="eam-field">
            <span>Warranty expiry</span>
            <DateInput value={warranty} onChange={setWarranty} disabled={busy} />
          </label>
        </div>

        <label className="eam-field eam-field-wide">
          <span>Location</span>
          <input
            type="text"
            className="form-input"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            maxLength={200}
            disabled={busy}
          />
        </label>

        <label className="eam-field eam-field-wide">
          <span>Notes</span>
          <textarea
            className="form-input eam-textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            maxLength={10_000}
            disabled={busy}
          />
        </label>

        <p className="eam-hint">Assignment is managed from the inventory list. Changes here are recorded in the audit log.</p>

        {topError && <p className="eam-error-banner" role="alert">{topError}</p>}
      </form>
    </Modal>
  );
}
